import { useMemo, useRef, useState } from "react";
import type { PlannerClusterNode, PlannerFamilyMeta } from "../../lib/api.js";

/**
 * Cluster map — the Planner's visual view. Deterministic layout (no force sim):
 * one full-width band per family, clusters packed in reading order with spokes
 * placed right after their pillar so parent→spoke edges stay short.
 *
 *   node size   = accepted keywords (sqrt scale; pending adds a dashed halo)
 *   node color  = action state (review amber / create brand / expand blue /
 *                 live emerald / planned gray) — same language as the tree chips
 *   solid edges = pillar → spoke (drawn inside the band)
 *   dashed edges= relatedClusters bridges (method×condition etc.) — shown for
 *                 the hovered/selected node only, to keep 183 nodes readable
 *
 * Wheel = zoom (about the cursor) · drag = pan · click = select (filters the
 * assignment queue, same as clicking a row in the tree).
 */

const BAND_W = 1160;
const NODE_GAP_X = 30;
const NODE_GAP_Y = 34;
const BAND_PAD_X = 16;
const BAND_HEADER = 26;

const ACTION_FILL: Record<string, string> = {
  review: "#f59e0b",        // amber
  "create-page": "#0d9488", // brand/teal
  "expand-page": "#3b82f6", // blue
  live: "#10b981",          // emerald
  planned: "#cbd5e1",       // slate-300
};

interface LaidNode {
  c: PlannerClusterNode;
  x: number;
  y: number;
  r: number;
  label: boolean;
}

interface Band {
  family: PlannerFamilyMeta;
  y: number;
  h: number;
  nodes: LaidNode[];
}

function radiusFor(c: PlannerClusterNode): number {
  return Math.min(14, 5 + Math.sqrt(c.keywordCount) * 2.2);
}

/** Pillars first, each followed by its spokes — keeps parent→spoke edges short. */
function familyOrder(list: PlannerClusterNode[]): PlannerClusterNode[] {
  const byParent = new Map<string, PlannerClusterNode[]>();
  const roots: PlannerClusterNode[] = [];
  const ids = new Set(list.map((c) => c.id));
  for (const c of list) {
    if (c.parentClusterId && ids.has(c.parentClusterId)) {
      const arr = byParent.get(c.parentClusterId) ?? [];
      arr.push(c);
      byParent.set(c.parentClusterId, arr);
    } else roots.push(c);
  }
  const out: PlannerClusterNode[] = [];
  const walk = (n: PlannerClusterNode) => {
    out.push(n);
    for (const child of byParent.get(n.id) ?? []) walk(child);
  };
  // Keep sub-families together, then roots by id for stability.
  roots.sort((a, b) => (a.subFamily ?? "").localeCompare(b.subFamily ?? "") || a.id.localeCompare(b.id));
  for (const r of roots) walk(r);
  return out;
}

function layout(families: PlannerFamilyMeta[], clusters: PlannerClusterNode[]): { bands: Band[]; height: number } {
  const perRow = Math.floor((BAND_W - BAND_PAD_X * 2) / NODE_GAP_X);
  const bands: Band[] = [];
  let y = 8;
  for (const f of families) {
    const list = familyOrder(clusters.filter((c) => c.family === f.id));
    if (list.length === 0) continue;
    const rows = Math.ceil(list.length / perRow);
    const h = BAND_HEADER + rows * NODE_GAP_Y + 8;
    const nodes: LaidNode[] = list.map((c, i) => ({
      c,
      x: BAND_PAD_X + NODE_GAP_X / 2 + (i % perRow) * NODE_GAP_X,
      y: y + BAND_HEADER + NODE_GAP_Y / 2 + Math.floor(i / perRow) * NODE_GAP_Y,
      r: radiusFor(c),
      label: c.keywordCount > 0 || c.pageType === "hub",
    }));
    bands.push({ family: f, y, h, nodes });
    y += h + 10;
  }
  return { bands, height: y + 4 };
}

export function PlannerMap({ families, clusters, selectedId, onSelect }: {
  families: PlannerFamilyMeta[];
  clusters: PlannerClusterNode[];
  selectedId: string | null;
  onSelect: (c: PlannerClusterNode) => void;
}) {
  const [hoverId, setHoverId] = useState<string | null>(null);
  const { bands, height } = useMemo(() => layout(families, clusters), [families, clusters]);
  const pos = useMemo(() => {
    const m = new Map<string, LaidNode>();
    for (const b of bands) for (const n of b.nodes) m.set(n.c.id, n);
    return m;
  }, [bands]);

  // viewBox zoom/pan
  const [view, setView] = useState({ x: 0, y: 0, w: BAND_W, h: 0 });
  const vb = view.h > 0 ? view : { x: 0, y: 0, w: BAND_W, h: height };
  const dragRef = useRef<{ sx: number; sy: number; vx: number; vy: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const clientToView = (e: React.MouseEvent | React.WheelEvent) => {
    const rect = svgRef.current!.getBoundingClientRect();
    return {
      x: vb.x + ((e.clientX - rect.left) / rect.width) * vb.w,
      y: vb.y + ((e.clientY - rect.top) / rect.height) * vb.h,
    };
  };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const p = clientToView(e);
    const k = e.deltaY > 0 ? 1.12 : 1 / 1.12;
    const w = Math.min(BAND_W * 1.4, Math.max(180, vb.w * k));
    const h = w * (vb.h / vb.w);
    setView({ x: p.x - ((p.x - vb.x) / vb.w) * w, y: p.y - ((p.y - vb.y) / vb.h) * h, w, h });
  };
  const onMouseDown = (e: React.MouseEvent) => { dragRef.current = { sx: e.clientX, sy: e.clientY, vx: vb.x, vy: vb.y }; };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    dragRef.current && setView({
      ...vb,
      x: dragRef.current.vx - ((e.clientX - dragRef.current.sx) / rect.width) * vb.w,
      y: dragRef.current.vy - ((e.clientY - dragRef.current.sy) / rect.height) * vb.h,
    });
  };
  const endDrag = () => { dragRef.current = null; };

  // Focus set: hovered or selected node + its pillar/spokes/related.
  const focusId = hoverId ?? selectedId;
  const focus = useMemo(() => {
    if (!focusId) return null;
    const c = pos.get(focusId)?.c;
    if (!c) return null;
    const set = new Set<string>([c.id]);
    if (c.parentClusterId) set.add(c.parentClusterId);
    for (const r of c.relatedClusters) set.add(r);
    for (const n of pos.values()) {
      if (n.c.parentClusterId === c.id) set.add(n.c.id);
      if (n.c.relatedClusters.includes(c.id)) set.add(n.c.id);
    }
    return set;
  }, [focusId, pos]);

  const hovered = hoverId ? pos.get(hoverId) : null;

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
        className="w-full cursor-grab active:cursor-grabbing select-none"
        style={{ maxHeight: "70vh" }}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={() => { endDrag(); setHoverId(null); }}
      >
        {/* family bands */}
        {bands.map((b) => (
          <g key={b.family.id}>
            <rect x={2} y={b.y} width={BAND_W - 4} height={b.h} rx={10}
              fill={b.family.money ? "#ecfdf5" : "#eff6ff"} stroke={b.family.money ? "#a7f3d0" : "#bfdbfe"} strokeWidth={1} />
            <text x={BAND_PAD_X} y={b.y + 17} fontSize={11} fontWeight={700} fill="#334155">
              {b.family.id} {b.family.money ? "· money" : "· editorial"}
            </text>
            <text x={BAND_W - BAND_PAD_X} y={b.y + 17} fontSize={10} fill="#64748b" textAnchor="end">
              {b.nodes.length} clusters · {b.nodes.reduce((s, n) => s + n.c.keywordCount, 0)} kw
            </text>
          </g>
        ))}

        {/* pillar → spoke edges */}
        {bands.flatMap((b) => b.nodes.map((n) => {
          const p = n.c.parentClusterId ? pos.get(n.c.parentClusterId) : null;
          if (!p) return null;
          const inFocus = !focus || focus.has(n.c.id) || focus.has(p.c.id);
          return (
            <path key={`e-${n.c.id}`}
              d={`M ${p.x} ${p.y} Q ${(p.x + n.x) / 2} ${Math.min(p.y, n.y) - 10} ${n.x} ${n.y}`}
              fill="none" stroke="#94a3b8" strokeWidth={focus && inFocus ? 1.4 : 0.6}
              opacity={focus ? (inFocus ? 0.9 : 0.08) : 0.3} />
          );
        }))}

        {/* related/bridge edges — only for the focused node */}
        {focusId && pos.get(focusId)?.c.relatedClusters.map((rid) => {
          const a = pos.get(focusId)!;
          const t = pos.get(rid);
          if (!t) return null;
          return (
            <path key={`r-${rid}`}
              d={`M ${a.x} ${a.y} Q ${(a.x + t.x) / 2 + 30} ${(a.y + t.y) / 2} ${t.x} ${t.y}`}
              fill="none" stroke="#0d9488" strokeWidth={1.4} strokeDasharray="4 3" opacity={0.85} />
          );
        })}

        {/* nodes */}
        {bands.flatMap((b) => b.nodes.map((n) => {
          const dim = focus ? !focus.has(n.c.id) : false;
          const isSel = n.c.id === selectedId;
          return (
            <g key={n.c.id} opacity={dim ? 0.22 : 1}
              onMouseEnter={() => setHoverId(n.c.id)}
              onMouseLeave={() => setHoverId((h) => (h === n.c.id ? null : h))}
              onClick={(e) => { e.stopPropagation(); onSelect(n.c); }}
              cursor="pointer">
              {n.c.pendingCount > 0 && (
                <circle cx={n.x} cy={n.y} r={n.r + 3.5} fill="none" stroke="#f59e0b" strokeWidth={1.4} strokeDasharray="2.5 2" />
              )}
              <circle cx={n.x} cy={n.y} r={n.r}
                fill={ACTION_FILL[n.c.action] ?? "#cbd5e1"}
                stroke={isSel ? "#0f172a" : n.c.pageUrl ? "#047857" : "#64748b"}
                strokeWidth={isSel ? 2.4 : n.c.pageUrl ? 1.8 : 0.8} />
              {n.c.pageType === "hub" && (
                <circle cx={n.x} cy={n.y} r={Math.max(1.8, n.r - 3.4)} fill="none" stroke="#ffffff" strokeWidth={1.1} opacity={0.9} />
              )}
              {(n.label || n.c.id === focusId) && (
                <text x={n.x} y={n.y + n.r + 9} fontSize={8.5} textAnchor="middle" fill="#334155" direction="rtl">
                  {n.c.nameHe.length > 22 ? n.c.nameHe.slice(0, 21) + "…" : n.c.nameHe}
                </text>
              )}
            </g>
          );
        }))}
      </svg>

      {/* hover card */}
      {hovered && (
        <div className="pointer-events-none absolute left-2 top-2 z-10 max-w-[300px] rounded-lg border border-line bg-white/95 px-2.5 py-1.5 text-xs shadow-md">
          <div dir="rtl" className="font-medium text-ink">{hovered.c.nameHe}</div>
          <div className="text-2xs text-muted">
            {hovered.c.id} · {hovered.c.pageType ?? "—"}{hovered.c.subFamily ? ` · ${hovered.c.subFamily}` : ""}
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-2 text-2xs text-muted">
            <span>{hovered.c.keywordCount} kw</span>
            {hovered.c.pendingCount > 0 && <span className="text-amber-600">{hovered.c.pendingCount} pending</span>}
            {hovered.c.totalVolume > 0 && <span>{hovered.c.totalVolume.toLocaleString()} vol</span>}
            {hovered.c.pageUrl && <span className="text-emerald-700">live page ✓</span>}
            {hovered.c.supplyGate != null && <span>gate {hovered.c.supplyGate}👥</span>}
            {hovered.c.phase && <span>P{hovered.c.phase}</span>}
          </div>
        </div>
      )}

      {/* legend */}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-2xs text-muted">
        {(["review", "create-page", "expand-page", "live", "planned"] as const).map((k) => (
          <span key={k} className="flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: ACTION_FILL[k] }} />
            {k === "create-page" ? "to create" : k === "expand-page" ? "to expand" : k}
          </span>
        ))}
        <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full border-2 border-emerald-700" /> has live page</span>
        <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full border border-dashed border-amber-500" /> pending halo</span>
        <span>◎ hub · scroll = zoom · drag = pan · hover = pillar/spoke + bridges · click = select</span>
      </div>
    </div>
  );
}
